/**
 * CONTEXT LAB — CodeAI101 chapter 2, what you sent and what arrived.
 *
 * FOUNDER, 2026-08-14: "ai101 labs next" → chapter 2.
 *
 * ── TWO PANES, AND THE GAP IS THE CHAPTER ─────────────────────────────────────────────────────
 * §2: "the AI does not 'remember less' — it physically cannot see the part that does not fit."
 * A learner nods at that and still believes, next month, that a forgetful chat means the AI is
 * tired. So the screen is WHAT YOU SENT beside WHAT THE MODEL SEES, and a block that will not fit
 * is struck through and greyed on the right while sitting perfectly intact on the left. Nothing
 * was forgotten. It never arrived.
 *
 * ── THE SIZES ARE HIS AND THE ARITHMETIC IS HIS ───────────────────────────────────────────────
 * Three windows, his three. The token count comes from the ratio implied by his own two size
 * pairs — 4,000/3,000 and 200,000/150,000, both exactly 4 tokens per 3 words — not from a real
 * tokenizer, which would give a different number and put this page quietly at odds with the one
 * beside it. Switching from the small model to the frontier one with the same messages loaded is
 * the second half of the lesson: nothing about the conversation changed.
 *
 * ── WHAT IT REFUSES ───────────────────────────────────────────────────────────────────────────
 * No API is called and no model is run. Every number on screen is arithmetic over his figures, and
 * the "answers" the two prompts would get are HIS descriptions of them, quoted — not generated. A
 * lab that called a real model to show the difference would be teaching that the difference is the
 * model, which is the one thing §4 says it is not.
 */

import { useMemo, useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  WINDOWS, VOICES, ONE_STREAM, tokensPerWord, countTokens, pack, positionNote,
  PHYSICALLY, NOT_TIRED, ORDER_MATTERS, NOT_THE_MODEL,
  PROMPT_A, PROMPT_B, A_GIVES, B_GIVES, WITHHOLDS, SOURCE_2, SOURCE_3, SOURCE_4,
} from './context.mjs';

/* A conversation with a long document in it — the ordinary shape that overflows a small window.
   The document's LENGTH is the point, so it is filler rather than prose: inventing 2,600 words of
   plausible-looking specification would be putting unsourced content on the page to make a size. */
const FILLER = (n) => Array.from({ length: n }, (_, i) => `line${i + 1}`).join(' ');

const START = [
  { id: 'sys', voice: 'system', label: 'System message', text: 'You are a helpful assistant for a grocery-tracking app. Be concise. Do not give financial advice.' },
  { id: 'doc', voice: 'user', label: 'A document you pasted in (2,600 words)', text: FILLER(2600) },
  { id: 'a1', voice: 'assistant', label: 'What the AI replied earlier (600 words)', text: FILLER(600) },
  { id: 'q', voice: 'user', label: 'Your question, just now', text: PROMPT_B },
];

export default function ContextLab() {
  const [win, setWin] = useState('small');
  const [blocks, setBlocks] = useState(START);
  const [mine, setMine] = useState('');
  const limit = WINDOWS.find((w) => w.id === win).tokens;
  const packed = useMemo(() => pack(blocks, limit), [blocks, limit]);
  const ratio = tokensPerWord();

  const drop = (id) => setBlocks((b) => b.filter((x) => x.id !== id));
  const reset = () => setBlocks(START);

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Context lab · what you sent, what arrived</div>
      <h2 className="g-h2 lab-h">It did not forget. It never saw it.</h2>
      <p className="g-body"><Value of={cited(PHYSICALLY, SOURCE_2)} /></p>

      {/* ── THE WINDOW ── */}
      <div className="lab-task-head">
        <span className="g-label">The model you are talking to</span>
        {WINDOWS.map((w) => (
          <button key={w.id} className={`g-btn ${win === w.id ? 'g-btn-primary' : ''}`}
            onClick={() => setWin(w.id)}>{w.tokens.toLocaleString()} tokens</button>
        ))}
      </div>
      <p className="g-body g-dim ctx-note">
        {WINDOWS.find((w) => w.id === win).label} — {WINDOWS.find((w) => w.id === win).note}
      </p>

      {/* ── THE METER ── */}
      <div className="ctx-meter" role="img"
        aria-label={`${packed.sentTokens} tokens sent, window holds ${limit}`}>
        <div className="ctx-fill" style={{ width: `${Math.min(100, (100 * packed.sentTokens) / limit)}%` }} />
        {packed.overflowed && <div className="ctx-over" />}
      </div>
      <div className="lab-readout">
        <div><span className="g-label">You sent</span><Value of={computed(`${packed.sentTokens.toLocaleString()} tokens`)} /></div>
        <div><span className="g-label">The window holds</span><Value of={computed(`${limit.toLocaleString()} tokens`)} /></div>
        <div><span className="g-label">The model saw</span><Value of={computed(`${packed.usedTokens.toLocaleString()} tokens`)} /></div>
      </div>

      {/* ── SENT vs SEEN ── */}
      <div className="ctx-two">
        <div className="ctx-pane">
          <div className="g-label">What you sent</div>
          {packed.sent.map((b) => (
            <div key={b.id} className="ctx-block">
              <div className="ctx-bhead">
                <span className={`ctx-voice ctx-${b.voice}`}>{b.voice}</span>
                <b>{b.label}</b>
                <span className="cou-sp" />
                <span className="cou-ms">{b.tokens.toLocaleString()}</span>
                {b.voice !== 'system' && (
                  <button className="blk-ico" onClick={() => drop(b.id)} aria-label="remove">×</button>
                )}
              </div>
            </div>
          ))}
          {/* PASTE YOUR OWN, because the token cost of YOUR text is the thing worth knowing. The
              founder asked whether the labs take input; here the answer was "you can only delete",
              which lets a learner watch the arithmetic without ever testing it on something of
              their own. */}
          <div className="lab-task-head" style={{ marginTop: 'var(--g-2)' }}>
            <input className="morse-input ctx-in" value={mine} placeholder="paste or type your own text…"
              onChange={(e) => setMine(e.target.value)} />
            <button className="g-btn" disabled={!mine.trim()}
              onClick={() => { setBlocks((b) => [...b, { id: `own-${b.length}`, voice: 'user',
                label: `Yours — ${mine.trim().split(/\s+/).length} words`, text: mine.trim() }]); setMine(''); }}>
              Add it
            </button>
            {mine.trim() && <span className="cou-ms">{countTokens(mine)} tokens</span>}
          </div>
          <button className="g-btn" style={{ marginTop: 'var(--g-2)' }} onClick={reset}>Put it all back</button>
        </div>

        <div className="ctx-pane">
          <div className="g-label">What the model sees</div>
          {packed.sent.map((b) => {
            const gone = packed.dropped.some((d) => d.id === b.id);
            return (
              <div key={b.id} className={`ctx-block ${gone ? 'ctx-gone' : ''}`}>
                <div className="ctx-bhead">
                  <span className={`ctx-voice ctx-${b.voice}`}>{b.voice}</span>
                  <b>{b.label}</b>
                  <span className="cou-sp" />
                  {gone ? <span className="cou-x">never arrived</span>
                    : <span className="cou-ms">{b.tokens.toLocaleString()}</span>}
                </div>
              </div>
            );
          })}
          {packed.overflowed
            ? <p className="g-body ctx-warn"><Value of={cited(NOT_TIRED, SOURCE_2)} /></p>
            : <p className="g-body g-dim">Everything fits. The model is reading all of it.</p>}
          <p className="g-body g-dim ctx-order">
            The last block is {positionNote}. <Value of={cited(ORDER_MATTERS, SOURCE_2)} />
          </p>
        </div>
      </div>

      {/* ── §4 — the concrete contrast, both prompts his ── */}
      <div className="lab-task">
        <div className="g-label">The same question, asked two ways</div>
        <div className="ctx-two">
          <div className="ctx-pane">
            <div className="g-label">Prompt A · {countTokens(PROMPT_A)} tokens</div>
            <p className="ctx-prompt">{PROMPT_A}</p>
            <p className="g-body g-dim"><Value of={cited(A_GIVES, SOURCE_4)} /></p>
          </div>
          <div className="ctx-pane">
            <div className="g-label">Prompt B · {countTokens(PROMPT_B)} tokens</div>
            <p className="ctx-prompt">{PROMPT_B}</p>
            <p className="g-body g-dim"><Value of={cited(B_GIVES, SOURCE_4)} /></p>
          </div>
        </div>
        <p className="g-body ctx-punch"><Value of={cited(NOT_THE_MODEL, SOURCE_4)} /></p>
        <p className="g-body g-dim"><Value of={cited(WITHHOLDS, SOURCE_4)} /></p>
      </div>

      {/* ── §3 — the three voices ── */}
      <details className="cou-tr">
        <summary className="g-label">Why some of it is labelled “system”</summary>
        <dl className="cou-dl">
          {VOICES.map((v) => <div key={v.id}><dt>{v.name}</dt><dd>{v.quote}</dd></div>)}
        </dl>
        <p className="g-body g-dim"><Value of={cited(ONE_STREAM, SOURCE_3)} /> The system block is
          the one you cannot delete above, because the product sets it for the whole conversation.</p>
      </details>

      <p className="g-body g-dim lab-why">
        No model is called and nothing is generated. The token counts come from the ratio implied by
        the module's own two figures — {ratio.from.join(' and ')}, both exactly{' '}
        {Math.round(ratio.value * 100) / 100} tokens per word — not from a real tokenizer, which
        would give a different number and put this page at odds with the one beside it. The two
        prompts are his, and what each would produce is his description of it, quoted. A lab that
        called a live model to show the difference would be teaching that the difference is the
        model — the one thing §4 says it is not.
      </p>
    </div>
  );
}
